import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import ContactsManager from './components/ContactsManager';
import AppLayout from './components/AppLayout';
import AuthForm from './components/AuthForm';
import CarlosMario from './components/CarlosMario';
import Planner from './components/Planner';
import SheetsManager from './components/SheetsManager';
import ChangePasswordModal from './components/ChangePasswordModal';
import AdminPanel from './components/AdminPanel';
import PreferenceCenter from './components/PreferenceCenter';
import ProcessDocs from './components/ProcessDocs';
import Journal from './components/Journal';
import Office from './components/Office';
import ResetPassword from './components/ResetPassword';
import './index.css';

const API_URL = process.env.REACT_APP_API_URL || '';
axios.defaults.baseURL = API_URL;

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const storedWsId = localStorage.getItem('active_workspace_id');
    if (storedWsId) {
      axios.defaults.headers.common['x-workspace-id'] = storedWsId;
    }
    if (!token) {
      setAuthChecked(true);
      return;
    }

    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    axios.get('/api/auth/me')
      .then((response) => {
        setUser(response.data.user);
      })
      .catch(() => {
        localStorage.removeItem('auth_token');
        delete axios.defaults.headers.common.Authorization;
      })
      .finally(() => setAuthChecked(true));
  }, []);

  const loadWorkspaces = useCallback(async () => {
    try {
      const response = await axios.get('/api/workspaces');
      setWorkspaces(response.data);
      
      const storedId = localStorage.getItem('active_workspace_id');
      let active = null;
      if (storedId) {
        active = response.data.find(w => String(w.id) === String(storedId));
      }
      if (!active && response.data.length > 0) {
        active = response.data[0];
      }
      
      if (active) {
        setActiveWorkspace(active);
        localStorage.setItem('active_workspace_id', active.id);
        axios.defaults.headers.common['x-workspace-id'] = active.id;
      }
    } catch (err) {
      console.error('Failed to load workspaces:', err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadWorkspaces();
    } else {
      setWorkspaces([]);
      setActiveWorkspace(null);
      delete axios.defaults.headers.common['x-workspace-id'];
    }
  }, [user, loadWorkspaces]);

  const handleWorkspaceChange = useCallback((workspaceId) => {
    const ws = workspaces.find(w => String(w.id) === String(workspaceId));
    if (ws) {
      setActiveWorkspace(ws);
      localStorage.setItem('active_workspace_id', ws.id);
      axios.defaults.headers.common['x-workspace-id'] = ws.id;
    }
  }, [workspaces]);

  const handleRefreshWorkspaces = useCallback(async () => {
    await loadWorkspaces();
  }, [loadWorkspaces]);

  const handleAuth = ({ token, user: userInfo }) => {
    localStorage.setItem('auth_token', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    setUser(userInfo);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    delete axios.defaults.headers.common.Authorization;
    localStorage.removeItem('active_workspace_id');
    delete axios.defaults.headers.common['x-workspace-id'];
    setUser(null);
  };

  // The reset-password page is the only route that bypasses the auth shell.
  return (
    <Routes>
      <Route path="/reset-password" element={<div className="app-root"><ResetPassword /></div>} />
      <Route
        path="/*"
        element={
          <div className="app-root">
            {!authChecked && (
              <div className="card" style={{ maxWidth: '520px', margin: '40px auto', textAlign: 'center' }}>
                Loading...
              </div>
            )}

            {authChecked && !user && <AuthForm onAuth={handleAuth} />}

            {authChecked && user && (
              <AppLayout
                user={user}
                onLogout={handleLogout}
                onChangePassword={() => setShowPasswordModal(true)}
                workspaces={workspaces}
                activeWorkspace={activeWorkspace}
                onWorkspaceChange={handleWorkspaceChange}
              >
                <Routes>
                  <Route path="/" element={<Navigate to="/agents" replace />} />
                  <Route path="/agents" element={<CarlosMario user={user} workspaceId={activeWorkspace?.id} />} />
                  <Route
                    path="/planner"
                    element={
                      <Planner
                        user={user}
                        workspaceId={activeWorkspace?.id}
                        onOpenSheet={(sheetId) => navigate(`/kanban?id=${sheetId}`)}
                      />
                    }
                  />
                  <Route path="/kanban" element={<SheetsManager user={user} workspaceId={activeWorkspace?.id} />} />
                  <Route path="/processes" element={<ProcessDocs user={user} workspaceId={activeWorkspace?.id} />} />
                  <Route path="/journal" element={<Journal user={user} workspaceId={activeWorkspace?.id} />} />
                  <Route path="/office" element={<Office user={user} workspaceId={activeWorkspace?.id} />} />
                  <Route path="/people" element={<ContactsManager />} />
                  <Route path="/admin" element={<AdminPanel user={user} workspaces={workspaces} onRefreshWorkspaces={handleRefreshWorkspaces} />} />
                  <Route
                    path="/preferences"
                    element={<PreferenceCenter user={user} onBack={() => navigate('/agents')} />}
                  />
                  <Route path="*" element={<Navigate to="/agents" replace />} />
                </Routes>
              </AppLayout>
            )}

            {showPasswordModal && (
              <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
            )}
          </div>
        }
      />
    </Routes>
  );
}

export default App;
